<template>
  <master-layout>
    <!-- Block Ionic swipe-back gesture -->
    <div class="swipe-back-blocker" @touchstart.stop @touchmove.stop></div>
    <!-- Scanner overlay -->
    <div v-if="scanning" class="scanner-overlay">
      <div class="swipe-back-blocker" @touchstart.stop @touchmove.stop></div>
      <div class="scanner-mask"></div>
      <div class="scanner-frame"></div>
      <!-- Back button -->
      <button class="scanner-back-btn" @click="stopScanAndGoBack">
        <ion-icon :icon="arrowBackOutline" class="text-[1.5rem] text-white"></ion-icon>
      </button>
      <div class="scanner-bottom-ui">
        <p class="text-white text-[1rem] font-semibold text-center">Point at the QR code</p>
        <!-- QR Code / Enter Code buttons on scanner -->
        <div class="flex justify-center gap-3 mt-6">
          <button
            class="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/20 text-[0.875rem] font-semibold text-white active:opacity-70"
            @click="stopScanAndShowQr"
          >
            <ion-icon :icon="qrCodeOutline" class="text-[1rem]"></ion-icon>
            My QR Code
          </button>
          <button
            class="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/20 text-[0.875rem] font-semibold text-white active:opacity-70"
            @click="stopScanAndShowCode"
          >
            <ion-icon :icon="keypadOutline" class="text-[1rem]"></ion-icon>
            Enter Code
          </button>
        </div>
      </div>
    </div>

    <div v-show="!scanning" class="px-5 pt-6 pb-[120px] flex flex-col min-h-full">
      <!-- Header -->
      <div class="photodrop-header flex items-center justify-between mb-4">
        <BackButton compact disableSafeArea :customBack="handleBack" />
      </div>
      <!-- Title row -->
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <h1 class="text-[2rem] leading-[2.5rem] font-black text-[#42362B] tracking-[-0.03em]">
            {{ activeMainTab === 1 && receiveTab !== 'scan' ? 'Receive Photos' : 'PhotoDrop' }}
          </h1>
          <button
            v-if="activeMainTab === 0 && !loading && !isPhotosEmpty && ttlSeen"
            class="w-[24px] h-[24px] flex items-center justify-center rounded-full active:opacity-60"
            @click="ttlTooltipVisible = !ttlTooltipVisible"
          >
            <ion-icon
              :icon="informationCircleOutline"
              class="text-[1.25rem] text-[#A09A93]"
            ></ion-icon>
          </button>
        </div>
        <div
          v-if="activeMainTab === 0 && !loading && !isPhotosEmpty"
          class="flex items-center gap-2"
        >
          <button
            v-if="!isSelecting"
            class="h-[32px] px-3 text-[0.875rem] font-semibold text-[#3D3A35] bg-[#EEECE8] rounded-full active:opacity-70"
            @click="enterSelectionMode(null)"
          >
            Select
          </button>
          <button
            v-else
            class="h-[32px] px-3 text-[0.875rem] font-semibold text-[#3D3A35] bg-[#EEECE8] rounded-full active:opacity-70"
            @click="exitSelectionMode"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Tab Content -->
      <div ref="tabContent" @touchstart.passive="onTabTouchStart" @touchend.passive="onTabTouchEnd">
        <!-- My Photos Tab -->
        <div v-if="activeMainTab === 0">
          <!-- Selection Bar (space always reserved) -->
          <div class="flex items-center justify-between mb-5" :class="{ invisible: !isSelecting }">
            <span class="text-[0.875rem] text-[#6B6B6B]">{{ selectedPhotos.size }} selected</span>
            <div class="flex items-center gap-2">
              <button
                class="flex items-center gap-1.5 px-4 py-2 rounded-full text-[0.8125rem] font-semibold"
                :class="
                  isDownloading
                    ? 'bg-[#3D3A35] text-white opacity-70'
                    : selectedPhotos.size > 0
                      ? 'bg-[#3D3A35] text-white active:opacity-80'
                      : 'bg-[#D6D3CF] text-[#9CA3AF]'
                "
                :disabled="selectedPhotos.size === 0 || isDownloading"
                @click="downloadSelectedPhotos"
              >
                <ion-spinner
                  v-if="isDownloading"
                  name="crescent"
                  class="text-white w-[14px] h-[14px]"
                ></ion-spinner>
                <ion-icon v-else :icon="downloadOutline" class="text-[0.875rem]"></ion-icon>
                {{ isDownloading ? 'Saving...' : 'Download' }}
              </button>
            </div>
          </div>

          <!-- Loading -->
          <Loading v-if="loading" />

          <!-- Empty State -->
          <div
            v-else-if="isPhotosEmpty"
            class="flex-1 flex flex-col items-center justify-center py-20"
          >
            <ion-icon :icon="imagesOutline" class="text-[3rem] text-[#D1D5DB] mb-4"></ion-icon>
            <p class="text-[1.125rem] font-semibold text-[#42362B] mb-1">No photos yet</p>
            <p class="text-[1rem] text-[#6B6B6B] text-center">
              Your photos will appear here after an event
            </p>
          </div>

          <!-- TTL Tooltip (subsequent visits) -->
          <div
            v-if="!loading && !isPhotosEmpty && ttlTooltipVisible"
            class="flex items-start gap-3 bg-[#FEF9EC] border border-[#F5E6B8] rounded-xl px-3.5 py-3 mb-5"
          >
            <ion-icon
              :icon="timeOutline"
              class="text-[1.125rem] text-[#B8860B] mt-0.5 shrink-0"
            ></ion-icon>
            <div class="flex-1">
              <p class="text-[0.8125rem] text-[#6B5A2E] leading-[1.125rem]">
                Your photos will be available for 1 year, do download your favourites before they
                disappear.
              </p>
            </div>
            <button
              class="text-[#B8860B] text-[1.125rem] leading-none mt-0.5 shrink-0 active:opacity-50"
              @click="ttlTooltipVisible = false"
            >
              &times;
            </button>
          </div>

          <!-- Expiring Soon indicator -->
          <button
            v-if="!loading && !isPhotosEmpty && expiringSoonCount > 0"
            class="flex items-center gap-2 w-full bg-[#FFF7ED] border border-[#FDBA74] rounded-xl px-3.5 py-3 mb-5 active:opacity-70"
            @click="scrollToExpiring"
          >
            <ion-icon
              :icon="timeOutline"
              class="text-[1.125rem] text-[#EA580C] shrink-0"
            ></ion-icon>
            <span class="flex-1 text-[0.8125rem] text-[#9A3412] font-semibold text-left">
              {{ expiringSoonCount }} photo{{ expiringSoonCount === 1 ? '' : 's' }} expiring soon
            </span>
            <ion-icon
              :icon="chevronDownOutline"
              class="text-[1rem] text-[#EA580C] shrink-0"
            ></ion-icon>
          </button>

          <!-- Photo Grid -->
          <div v-if="!loading && !isPhotosEmpty">
            <div
              v-for="(day, dayIndex) in photos"
              :key="dayIndex"
              :ref="'day-' + dayIndex"
              class="mb-6"
            >
              <div class="flex items-center gap-2 mb-3">
                <h2 class="text-[1.125rem] font-bold text-[#42362B]">{{ formatDate(day.date) }}</h2>
                <span
                  v-if="dayExpiringSoonCount(day) > 0"
                  class="inline-flex items-center gap-1 bg-[#FFF7ED] text-[#EA580C] text-[0.6875rem] font-semibold px-2 py-0.5 rounded-full"
                >
                  <ion-icon :icon="timeOutline" class="text-[0.625rem]"></ion-icon>
                  {{ dayExpiringSoonCount(day) }} expiring
                </span>
              </div>
              <div class="grid grid-cols-3 gap-[2px]">
                <div
                  v-for="(photo, imgIndex) in day.photos"
                  :key="imgIndex"
                  class="photo-tile relative aspect-square cursor-pointer overflow-hidden"
                  :data-image="photo.link"
                  :class="{ 'photo-tile--selectable': isSelecting }"
                  @click="handleClick(dayIndex, imgIndex)"
                  @touchstart="handleTouchStart(dayIndex, imgIndex, $event)"
                  @touchend="handleTouchEnd(dayIndex, imgIndex)"
                  @touchmove="handleTouchCancel($event)"
                  @contextmenu.prevent
                >
                  <video
                    v-if="isVideo(photo.link)"
                    class="w-full h-full object-cover pointer-events-none select-none"
                    :src="photo.link"
                    autoplay
                    loop
                    muted
                    playsinline
                    preload="auto"
                  />
                  <img
                    v-else
                    class="w-full h-full object-cover pointer-events-none select-none"
                    draggable="false"
                    :src="photo.link"
                  />
                  <!-- Expiry warning badge (≤30 days) -->
                  <div
                    v-if="daysUntilExpiry(photo.expiresAt) <= 30"
                    class="absolute top-1 left-1 flex items-center gap-0.5 bg-black/60 rounded-full px-1.5 py-0.5"
                  >
                    <ion-icon :icon="timeOutline" class="text-[0.625rem] text-[#FBBF24]"></ion-icon>
                    <span class="text-[0.5625rem] text-[#FBBF24] font-semibold leading-none"
                      >{{ daysUntilExpiry(photo.expiresAt) }}d</span
                    >
                  </div>
                  <div
                    v-if="selectedPhotos.has(photo.link)"
                    class="selection-check absolute bottom-1.5 right-1.5 w-[24px] h-[24px] rounded-full flex items-center justify-center bg-[#3D3A35]"
                  >
                    <ion-icon
                      :icon="checkmarkOutline"
                      class="text-[0.8125rem] text-white"
                    ></ion-icon>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Receive Tab -->
        <div v-if="activeMainTab === 1">
          <!-- Claim Success -->
          <div v-if="hasClaimed" class="flex flex-col items-center justify-center min-h-[40vh]">
            <div
              class="w-[64px] h-[64px] bg-[#E8F5E9] rounded-full flex items-center justify-center mb-6"
            >
              <ion-icon
                :icon="checkmarkCircleOutline"
                class="text-[2rem] text-[#4CAF50]"
              ></ion-icon>
            </div>
            <h2 class="text-[1.5rem] font-bold leading-[2rem] text-center mb-10">
              {{ photoCount }} photo{{ photoCount === 1 ? '' : 's' }} added!
            </h2>
            <button
              class="w-full max-w-[320px] py-[16px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold"
              @click="
                $store.commit('photobooth/reset');
                setMainTab(0);
              "
            >
              View Photos
            </button>
          </div>

          <!-- Scanner view: shows error or loading after scan, auto-starts scanner -->
          <template v-else-if="receiveTab === 'scan'">
            <!-- Loading -->
            <div v-if="claimLoading" class="flex flex-col items-center justify-center min-h-[40vh]">
              <ion-spinner name="crescent" class="text-[#3D3A35] w-[32px] h-[32px]"></ion-spinner>
              <p class="text-[0.875rem] text-[#6B6B6B] mt-3">Fetching your photos...</p>
            </div>

            <!-- Error -->
            <div
              v-else-if="claimError"
              class="flex flex-col items-center justify-center min-h-[40vh]"
            >
              <p class="text-[0.875rem] text-[#EC4E20] text-center mb-6">{{ claimError }}</p>
              <button
                class="w-full max-w-[320px] py-[16px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold"
                @click="startScan"
              >
                Scan Again
              </button>
            </div>

            <!-- Bottom toggle buttons -->
            <div class="flex justify-center gap-3 mt-8">
              <button
                class="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#EEECE8] text-[0.875rem] font-semibold text-[#3D3A35] active:opacity-70"
                @click="setReceiveTab('qr')"
              >
                <ion-icon :icon="qrCodeOutline" class="text-[1rem]"></ion-icon>
                My QR Code
              </button>
              <button
                class="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#EEECE8] text-[0.875rem] font-semibold text-[#3D3A35] active:opacity-70"
                @click="setReceiveTab('code')"
              >
                <ion-icon :icon="keypadOutline" class="text-[1rem]"></ion-icon>
                Enter Code
              </button>
            </div>
          </template>

          <!-- QR Code view -->
          <template v-else-if="receiveTab === 'qr'">
            <div class="flex flex-col items-center">
              <div v-if="profileLoading || fetchingQrToken" class="pt-20">Loading...</div>
              <template v-else-if="personalId">
                <p class="text-[1rem] leading-[1.5rem] text-[#6B6B6B] text-center mb-8">
                  Show this QR code to a photographer to receive your photos
                </p>
                <div class="bg-white rounded-[20px] p-10 flex items-center justify-center">
                  <qrcode-vue v-if="qrValue" :value="qrValue" :size="230" level="H" />
                  <p
                    v-else
                    data-testid="qr-not-linked"
                    class="text-center text-[1rem] leading-[1.5rem] text-[#2C2E34]"
                  >
                    Your QR code will be ready once your account is linked to your church record.
                  </p>
                </div>
                <p class="text-[1rem] leading-[1.5rem] font-semibold mt-4">
                  Personal ID: {{ personalId }}
                </p>
              </template>
              <div v-else class="pt-10 text-center">
                <p class="text-[1rem] text-[#6B6B6B] mb-6">{{ qrErrorMsg }}</p>
                <button
                  v-if="!dob || !uin"
                  class="h-[48px] px-8 bg-[#3D3A35] text-white rounded-[36px] text-[1rem] font-semibold"
                  @click="$router.push('/profile')"
                >
                  Edit Profile
                </button>
              </div>
            </div>
          </template>

          <!-- Number Code view -->
          <template v-else-if="receiveTab === 'code'">
            <div class="flex flex-col items-center pt-4">
              <p class="text-[1rem] leading-[1.5rem] text-[#6B6B6B] text-center mb-10">
                Enter the 6-digit code shown on the photobooth screen
              </p>
              <div class="flex justify-center mb-6" style="width: 90vw; max-width: 320px">
                <v-otp-input
                  ref="otpInput"
                  input-classes="photobooth-otp-input"
                  input-type="tel"
                  inputmode="numeric"
                  separator=""
                  :num-inputs="6"
                  :should-auto-focus="false"
                  :should-focus-order="true"
                  :is-disabled="claimLoading"
                  @on-complete="handleCodeComplete"
                />
              </div>
              <p v-if="claimError" class="text-[0.875rem] italic text-[#EC4E20] text-center mb-4">
                {{ claimError }}
              </p>
              <Loading v-if="claimLoading" variant="inline" />
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Floating Tab Bar -->
    <div v-show="activeMainTab === 0" class="photodrop-floating-bar">
      <button
        class="photodrop-float-tab"
        :class="{ 'photodrop-float-tab--active': activeMainTab === 0 }"
        @click="setMainTab(0)"
      >
        <ion-icon :icon="imagesOutline" class="text-[1.125rem]"></ion-icon>
        <span>My Photos</span>
      </button>
      <button
        class="photodrop-float-tab"
        :class="{ 'photodrop-float-tab--active': activeMainTab === 1 }"
        @click="setMainTab(1)"
      >
        <ion-icon :icon="scanOutline" class="text-[1.125rem]"></ion-icon>
        <span>Receive</span>
      </button>
    </div>

    <!-- Long-press peek preview (iOS-style) -->
    <Teleport to="body">
      <div
        v-if="popupVisible"
        class="peek-overlay"
        @click="closePopup"
        @touchstart.stop
        @touchmove.stop
        @touchend.stop
      >
        <div class="peek-content" @click.stop>
          <!-- Large photo preview -->
          <div class="peek-image">
            <video
              v-if="isVideo(popupImage)"
              class="w-full h-full object-cover"
              :src="popupImage"
              autoplay
              loop
              muted
              playsinline
            />
            <img v-else class="w-full h-full object-cover" :src="popupImage" />
          </div>
          <!-- Actions below -->
          <div class="peek-actions">
            <button class="peek-action" @click="popupSelect">
              <ion-icon :icon="checkmarkCircleOutline" class="text-[1.25rem]"></ion-icon>
              <span>Select</span>
            </button>
            <div class="peek-divider"></div>
            <button class="peek-action" @click="popupDownload">
              <ion-icon :icon="downloadOutline" class="text-[1.25rem]"></ion-icon>
              <span>Download</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Lightbox -->
    <Teleport to="body">
      <div
        v-if="lightboxOpen"
        class="fixed inset-0 bg-black z-[9990] flex flex-col"
        @click="closeLightbox"
      >
        <!-- Header -->
        <div class="px-4" :style="lightboxHeaderStyle" @click.stop>
          <div class="flex items-center justify-between">
            <button
              type="button"
              class="size-10 flex items-center justify-center rounded-full bg-white/20 active:opacity-70"
              @click="closeLightbox"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15 5L5 15M5 5l10 10"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <span class="text-white text-[0.875rem] font-semibold">
              {{ lightboxDisplayIndex }} / {{ flatPhotos.length }}
            </span>
            <div class="size-10" />
          </div>
        </div>

        <!-- Swipeable Image Area -->
        <div class="flex-1 overflow-hidden lightbox-swiper-area" @click.stop>
          <BaseSwiper
            ref="lightboxSwiper"
            :id="lightboxSwiperId"
            :items="flatPhotos"
            :options="lightboxSwiperOptions"
            :gap="0"
            class="w-full h-full"
            @active-index-change="handleLightboxSlideChange"
          >
            <template #default="slotProps">
              <div class="w-full h-full flex items-center justify-center px-4">
                <video
                  v-if="isVideo(slotProps.item.link)"
                  :src="slotProps.item.link"
                  class="max-w-full max-h-full object-contain select-none rounded-lg"
                  controls
                  autoplay
                  loop
                  playsinline
                  preload="auto"
                />
                <img
                  v-else
                  :src="slotProps.item.link"
                  class="max-w-full max-h-full object-contain select-none pointer-events-none rounded-lg"
                  draggable="false"
                />
              </div>
            </template>
          </BaseSwiper>
        </div>

        <!-- Footer: Download -->
        <div class="pb-4 pt-2 flex justify-center" @click.stop>
          <button
            type="button"
            class="flex items-center gap-2 rounded-full border border-white/40 bg-white/15 px-5 py-2.5 active:opacity-70"
            :disabled="lightboxDownloading"
            @click="downloadCurrentLightboxPhoto"
          >
            <div
              v-if="lightboxDownloading"
              class="size-5 border-2 border-white border-t-transparent rounded-full animate-spin"
            />
            <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 3v10M6 9l4 4 4-4M3 15v1a1 1 0 001 1h12a1 1 0 001-1v-1"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="text-white text-[0.9375rem] font-semibold">
              {{ lightboxDownloading ? 'Saving...' : 'Download' }}
            </span>
          </button>
        </div>

        <!-- Lightbox inline toast -->
        <div
          v-if="lightboxToast"
          class="lightbox-toast"
          :class="lightboxToastError ? 'bg-[#BE3A34]' : 'bg-[#08A045]'"
        >
          {{ lightboxToast }}
        </div>
      </div>
    </Teleport>

    <!-- TTL First Visit Popup -->
    <ModalSheet v-model="showTtlPopup">
      <div class="flex flex-col items-center text-center px-2">
        <div
          class="w-[56px] h-[56px] bg-[#FEF9EC] rounded-full flex items-center justify-center mb-5"
        >
          <ion-icon :icon="timeOutline" class="text-[1.75rem] text-[#B8860B]"></ion-icon>
        </div>
        <h2 class="text-[1.375rem] font-bold text-[#42362B] mb-2">Photo Storage</h2>
        <p class="text-[0.9375rem] text-[#6B6B6B] leading-[1.375rem] mb-8">
          Your photos will be available for 1 year, do download your favourites before they
          disappear.
        </p>
        <button
          class="w-full max-w-[320px] py-[14px] bg-[#3D3A35] text-white rounded-full text-[1rem] font-semibold active:opacity-80"
          @click="dismissTtlPopup"
        >
          Got it
        </button>
      </div>
    </ModalSheet>
  </master-layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import axios from 'axios';
import { Media } from '@capacitor-community/media';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { isPlatform, toastController } from '@ionic/vue';
import {
  imagesOutline,
  checkmarkOutline,
  checkmarkCircleOutline,
  chevronDownOutline,
  downloadOutline,
  scanOutline,
  qrCodeOutline,
  keypadOutline,
  arrowBackOutline,
  timeOutline,
  informationCircleOutline,
} from 'ionicons/icons';
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import QrcodeVue from 'qrcode.vue';
import VOtpInput from 'vue3-otp-input';
import { triggerNotification, triggerImpact } from '@/utils/haptics';
import { isIosNativeOnly, isAndroidNativeOnly, isTablet } from '@/utils/platform-check';
import BackButton from '@/components/common/BackButton.vue';
import Loading from '@/components/common/LoadingState.vue';
import BaseSwiper from '@/components/design/BaseSwiper.vue';
import ModalSheet from '@/components/common/ModalSheet.vue';
import ClaimSuccess from '@/pages/photodrop/ClaimSuccess.vue';
import { SINGLE_ITEM } from '@/utils/swiper-presets';

const LONG_PRESS_DURATION = 500;
const ALBUM_NAME = 'HOGC One';
const TTL_BANNER_KEY = 'photodrop-ttl-banner-dismissed';

export default {
  name: 'PhotodropPage',
  components: { BackButton, Loading, BaseSwiper, ModalSheet, ClaimSuccess, QrcodeVue, VOtpInput },
  data() {
    return {
      activeMainTab: 0,
      receiveTab: 'scan',
      scanning: false,
      tabTouchStartX: null,
      tabTouchStartY: null,
      isSelecting: false,
      selectedPhotos: new Set(),
      longPressTimer: null,
      longPressImgIndex: null,
      touchMoved: false,
      touchStartX: 0,
      touchStartY: 0,
      isDragSelecting: false,
      dragPending: false,
      dragStartedSelecting: false,
      lastDragSelectedImage: null,
      autoScrollTimer: null,
      autoScrollSpeed: 0,
      scrollElement: null,
      isDownloading: false,
      lightboxDownloading: false,
      lightboxToast: null,
      lightboxToastError: false,
      lightboxToastTimer: null,
      // Long-press popup
      popupVisible: false,
      popupImage: null,
      // Lightbox
      lightboxOpen: false,
      lightboxDayIndex: null,
      lightboxImgIndex: 0,
      lightboxSwiperId: `lightbox-${Math.random().toString(36).substring(2, 11)}`,
      ttlSeen: localStorage.getItem(TTL_BANNER_KEY) === 'true',
      showTtlPopup: false,
      ttlTooltipVisible: false,
      encryptedQrToken: null,
      fetchingQrToken: false,
    };
  },
  computed: {
    ...mapState('photos', ['photos', 'loading']),
    ...mapGetters('photos', ['isPhotosEmpty']),
    ...mapState('profile', {
      profileLoading: 'loading',
      dob: 'dob',
      uin: 'uin',
      personalId: 'personalId',
      hasNoMember: 'hasNoMember',
    }),
    // A login with no member never falls back to the raw personal ID: it is self-typed,
    // and a reader of the raw ID would take it for the member who owns that ID.
    qrValue() {
      if (this.encryptedQrToken) return this.encryptedQrToken;
      return this.hasNoMember ? '' : this.personalId || '';
    },
    ...mapState('photobooth', { claimLoading: 'loading', claimError: 'error' }),
    ...mapGetters('photobooth', ['hasClaimed', 'photoCount']),
    qrErrorMsg() {
      if (!this.dob && !this.uin) {
        return 'Update your last 4 characters of your NRIC/FIN and Date of Birth under Edit Profile to view your QR code.';
      }
      if (!this.dob) return 'Update your Date of Birth under Edit Profile to view your QR code.';
      if (!this.uin)
        return 'Update your last 4 characters of your NRIC/FIN under Edit Profile to view your QR code.';
      return 'There was an issue generating your Personal ID. Our IT team will be in touch.';
    },
    isNativePlatform() {
      return (isPlatform('android') || isPlatform('ios')) && !isPlatform('mobileweb');
    },
    flatPhotos() {
      return this.photos.flatMap(day => day.photos);
    },
    expiringSoonCount() {
      return this.flatPhotos.filter(p => this.daysUntilExpiry(p.expiresAt) <= 30).length;
    },
    firstExpiringDayIndex() {
      return this.photos.findIndex(day =>
        day.photos.some(p => this.daysUntilExpiry(p.expiresAt) <= 30),
      );
    },
    lightboxFlatIndex() {
      if (this.lightboxDayIndex === null) return 0;
      let offset = 0;
      for (let i = 0; i < this.lightboxDayIndex; i++) {
        offset += this.photos[i].photos.length;
      }
      return offset + this.lightboxImgIndex;
    },
    lightboxCurrentImage() {
      return this.flatPhotos[this.lightboxFlatIndex]?.link || '';
    },
    lightboxDisplayIndex() {
      return this.lightboxFlatIndex + 1;
    },
    lightboxSwiperOptions() {
      return {
        ...SINGLE_ITEM,
        initialSlide: this.lightboxFlatIndex,
        hasNavigation: false,
      };
    },
    lightboxHeaderStyle() {
      let paddingTop = '32px';
      if (isIosNativeOnly() || isTablet()) {
        paddingTop = 'calc(32px + env(safe-area-inset-top, 0px))';
      } else if (isAndroidNativeOnly()) {
        paddingTop = 'max(32px, env(safe-area-inset-top, 32px))';
      }
      return { paddingTop };
    },
  },
  watch: {
    photos() {
      this.$nextTick(() => this.playAllVideos());
    },
    personalId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchEncryptedQrToken();
      },
    },
  },
  async ionViewWillEnter() {
    // Disable swipe-back gesture on this page
    const outlet = document.querySelector('ion-router-outlet');
    if (outlet) outlet.swipeGesture = false;

    await this.$store.dispatch('photos/fetchPhotos');
    if (!this.ttlSeen && !this.isPhotosEmpty) {
      this.showTtlPopup = true;
    }
    this.$store.commit('photobooth/reset');
    this.$nextTick(() => {
      this.playAllVideos();
      this.cacheScrollElement();
    });
  },
  ionViewWillLeave() {
    this.stopScan();
    // Re-enable swipe-back gesture when leaving
    const outlet = document.querySelector('ion-router-outlet');
    if (outlet) outlet.swipeGesture = true;
  },
  created() {
    Object.assign(this, {
      imagesOutline,
      checkmarkOutline,
      checkmarkCircleOutline,
      chevronDownOutline,
      downloadOutline,
      scanOutline,
      qrCodeOutline,
      keypadOutline,
      arrowBackOutline,
      timeOutline,
      informationCircleOutline,
    });
  },
  methods: {
    handleBack() {
      if (this.activeMainTab === 1) {
        this.$store.commit('photobooth/reset');
        this.setMainTab(0);
      } else {
        this.$router.go(-1);
      }
    },
    async showToast(message, background = '#08A045') {
      // Dismiss any existing toast first
      try {
        await toastController.dismiss();
      } catch {
        /* none active */
      }
      const toast = await toastController.create({
        message,
        duration: 2500,
        position: 'bottom',
        cssClass: 'photodrop-toast',
        mode: 'ios',
      });
      const shadowRoot = toast.shadowRoot;
      if (shadowRoot) {
        const style = document.createElement('style');
        style.textContent = `.toast-wrapper { background: ${background} !important; --background: ${background} !important; color: #fff !important; }`;
        shadowRoot.appendChild(style);
      }
      toast.style.zIndex = '99999';
      toast.style.setProperty('--bottom', '88px');
      await toast.present();
    },
    playAllVideos() {
      const videos = this.$el?.querySelectorAll('.photo-tile video');
      if (!videos) return;
      videos.forEach(v => {
        v.play().catch(() => {});
      });
    },
    setMainTab(index) {
      if (index === 0) this.exitSelectionMode();
      if (this.scanning) this.stopScan();
      this.activeMainTab = index;
      if (index === 1) {
        this.receiveTab = 'scan';
        this.$store.commit('photobooth/reset');
        if (!this.hasClaimed) {
          this.$nextTick(() => this.startScan());
        }
      }
    },
    setReceiveTab(tab) {
      if (tab === this.receiveTab) return;
      if (this.scanning) this.stopScan();
      this.$store.commit('photobooth/reset');
      this.receiveTab = tab;
    },
    onTabTouchStart(e) {
      if (this.isSelecting || this.isDragSelecting || this.scanning) return;
      if (this.activeMainTab === 1 && this.receiveTab !== 'scan') return;
      const touch = e.changedTouches?.[0];
      if (!touch) return;
      this.tabTouchStartX = touch.clientX;
      this.tabTouchStartY = touch.clientY;
    },
    onTabTouchEnd(e) {
      if (this.isSelecting || this.isDragSelecting) return;
      const touch = e.changedTouches?.[0];
      if (!touch || this.tabTouchStartX === null) return;
      const dx = touch.clientX - this.tabTouchStartX;
      const dy = touch.clientY - this.tabTouchStartY;
      this.tabTouchStartX = null;
      this.tabTouchStartY = null;
      if (Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy)) return;
      if (dx < 0 && this.activeMainTab === 0) this.setMainTab(1);
      else if (dx > 0 && this.activeMainTab === 1) this.setMainTab(0);
    },
    // Receive tab methods
    async handleCodeComplete(code) {
      await this.$store.dispatch('photobooth/claimCode', code);
      if (this.hasClaimed) {
        await this.$store.dispatch('photos/fetchPhotos');
      }
      if (this.claimError && this.$refs.otpInput) {
        this.$refs.otpInput.clearInput();
      }
    },
    resetClaim() {
      this.$store.commit('photobooth/reset');
      this.$nextTick(() => {
        if (this.$refs.otpInput) this.$refs.otpInput.clearInput();
      });
    },
    async startScan() {
      try {
        const status = await BarcodeScanner.checkPermission({ force: true });
        if (!status.granted) {
          this.$store.dispatch('toast/error', 'Camera permission is required to scan QR codes.');
          return;
        }
        this.scanning = true;
        document.querySelector('body').classList.add('scanner-active');
        const ionApp = document.querySelector('ion-app');
        if (ionApp) ionApp.classList.add('scanner-active');
        // Awaited (#1226): unawaited, a rejection here escapes this try/catch
        // as an unhandled promise rejection, so the webview is left transparent
        // with no toast and no cleanup. The catch below is the path for it.
        await BarcodeScanner.hideBackground();
        const result = await BarcodeScanner.startScan();
        this.cleanupScanner();
        if (result.hasContent) {
          const scanned = result.content;

          // 6-digit code from photographer's "Generate Code" flow
          if (/^\d{6}$/.test(scanned)) {
            await this.$store.dispatch('photobooth/claimCode', scanned);
            if (this.$store.getters['photobooth/hasClaimed']) {
              const count = this.$store.getters['photobooth/photoCount'];
              this.$store.dispatch(
                'toast/success',
                `${count} photo${count === 1 ? '' : 's'} added!`,
              );
              this.$store.dispatch('photos/fetchPhotos');
            }
            return;
          }

          // Fotoshare URL
          if (/^https:\/\/([\w-]+\.)?fotoshare\.co\//.test(scanned)) {
            await this.$store.dispatch('photobooth/claimFotoshare', scanned);
            if (this.$store.getters['photobooth/hasClaimed']) {
              const count = this.$store.getters['photobooth/photoCount'];
              this.$store.dispatch(
                'toast/success',
                `${count} photo${count === 1 ? '' : 's'} added!`,
              );
              this.$store.dispatch('photos/fetchPhotos');
            } else if (this.$store.state.photobooth.error) {
              this.$store.commit('photobooth/reset');
            }
            return;
          }

          // Encrypted member QR token (iv.ciphertext.tag format)
          if (/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(scanned)) {
            this.$store.dispatch(
              'toast/error',
              'This is a member QR code. Use the photographer app to scan member QR codes.',
            );
            return;
          }

          this.$store.dispatch('toast/error', 'Unrecognized QR code format.');
        }
      } catch {
        this.cleanupScanner();
        this.$store.dispatch('toast/error', 'Scanner error. Please try again.');
      }
    },
    cleanupScanner() {
      document.querySelector('body').classList.remove('scanner-active');
      const ionApp = document.querySelector('ion-app');
      if (ionApp) ionApp.classList.remove('scanner-active');
      BarcodeScanner.showBackground();
      this.scanning = false;
    },
    stopScanAndGoBack() {
      this.stopScan();
      this.setMainTab(0);
    },
    stopScanAndShowQr() {
      this.stopScan();
      this.receiveTab = 'qr';
    },
    stopScanAndShowCode() {
      this.stopScan();
      this.receiveTab = 'code';
    },
    stopScan() {
      if (this.scanning) {
        BarcodeScanner.stopScan();
        this.cleanupScanner();
      }
    },
    isVideo(url) {
      if (!url) return false;
      // S3 keys have .mp4 extension; presigned URLs have the key before the query params
      const path = url.split('?')[0];
      return path.toLowerCase().endsWith('.mp4');
    },
    formatDate(date) {
      const d = new Date(date);
      const weekday = d.toLocaleDateString('en-us', { weekday: 'short' });
      const month = d.toLocaleDateString('en-us', { month: 'short' });
      const day = d.getDate();
      const year = d.getFullYear();
      return `${weekday}, ${month} ${day} ${year}`;
    },

    // Touch handling — passes imgIndex (not image URL) for lightbox
    handleTouchStart(dayIndex, imgIndex, e) {
      this.touchMoved = false;
      if (e.touches.length === 1) {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
      }
      if (this.isSelecting) {
        this.isDragSelecting = false;
        this.dragPending = true;
        this.lastDragSelectedImage = this.photos[dayIndex].photos[imgIndex]?.link ?? null;
        this.dragStartedSelecting = false;
        return;
      }
      this.longPressImgIndex = null;
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        this.longPressImgIndex = imgIndex;
        triggerImpact('heavy');
        this.showPopup(dayIndex, imgIndex);
      }, LONG_PRESS_DURATION);
    },
    handleTouchEnd(dayIndex, imgIndex) {
      if (this.isDragSelecting || this.dragPending) {
        this.isDragSelecting = false;
        this.dragPending = false;
        this.lastDragSelectedImage = null;
        this.stopAutoScroll();
        if (!this.touchMoved) {
          const link = this.photos[dayIndex].photos[imgIndex]?.link;
          if (link) this.toggleSelection(link);
          triggerImpact('light');
        }
        return;
      }
      this.cancelLongPress();
      if (this.touchMoved) return;
      if (this.longPressImgIndex === imgIndex) {
        this.longPressImgIndex = null;
        return;
      }
      this.handleTap(dayIndex, imgIndex);
    },
    selectRow(image) {
      // Find which day and index this image belongs to, then select its entire row
      for (const day of this.photos) {
        const idx = day.photos.findIndex(p => p.link === image);
        if (idx === -1) continue;
        const rowStart = idx - (idx % 3);
        const rowEnd = Math.min(rowStart + 3, day.photos.length);
        const next = new Set(this.selectedPhotos);
        let added = false;
        for (let i = rowStart; i < rowEnd; i++) {
          const link = day.photos[i]?.link;
          if (link && !next.has(link)) {
            next.add(link);
            added = true;
          }
        }
        if (added) {
          this.selectedPhotos = next;
          triggerImpact('medium');
        }
        return;
      }
    },
    async startAutoScroll(touchY) {
      const edgeZone = 120;
      const screenH = window.innerHeight;
      let speed = 0;
      if (touchY > screenH - edgeZone) {
        speed = Math.min(20, ((touchY - (screenH - edgeZone)) / edgeZone) * 20);
      } else if (touchY < edgeZone) {
        speed = -Math.min(20, ((edgeZone - touchY) / edgeZone) * 20);
      }
      this.autoScrollSpeed = speed;
      // Ensure scroll element is cached
      if (!this.scrollElement) await this.cacheScrollElement();
      if (speed !== 0 && !this.autoScrollTimer) {
        this.autoScrollTimer = setInterval(() => {
          if (this.scrollElement && this.autoScrollSpeed) {
            this.scrollElement.scrollTop += this.autoScrollSpeed;
          }
        }, 16);
      } else if (speed === 0) {
        this.stopAutoScroll();
      }
    },
    stopAutoScroll() {
      if (this.autoScrollTimer) {
        clearInterval(this.autoScrollTimer);
        this.autoScrollTimer = null;
      }
      this.autoScrollSpeed = 0;
    },
    async cacheScrollElement() {
      const content = this.$el?.closest('ion-content');
      if (content) {
        this.scrollElement = await content.getScrollElement();
      }
    },
    selectTileAtPoint(clientX, clientY) {
      const el = document.elementFromPoint(clientX, clientY);
      const tile = el?.closest('.photo-tile');
      if (!tile) return;
      const image = tile.dataset.image;
      if (!image || image === this.lastDragSelectedImage) return;
      this.lastDragSelectedImage = image;
      if (this.isLastInRow(image)) {
        this.selectRow(image);
      } else if (!this.selectedPhotos.has(image)) {
        const next = new Set(this.selectedPhotos);
        next.add(image);
        this.selectedPhotos = next;
        triggerImpact('light');
      }
    },
    onGlobalTouchMove(e) {
      if (!this.isDragSelecting || !e.touches.length) return;
      e.preventDefault();
      const touch = e.touches[0];
      this.startAutoScroll(touch.clientY);
      this.selectTileAtPoint(touch.clientX, touch.clientY);
    },
    onGlobalTouchEnd() {
      if (this.isDragSelecting || this.dragPending) {
        this.isDragSelecting = false;
        this.dragPending = false;
        this.lastDragSelectedImage = null;
        this.stopAutoScroll();
      }
      document.removeEventListener('touchmove', this.onGlobalTouchMove);
      document.removeEventListener('touchend', this.onGlobalTouchEnd);
    },
    isLastInRow(image) {
      for (const day of this.photos) {
        const idx = day.photos.findIndex(p => p.link === image);
        if (idx === -1) continue;
        const rowStart = idx - (idx % 3);
        const rowEnd = Math.min(rowStart + 3, day.photos.length);
        const rowSize = rowEnd - rowStart;
        // Last column in a full row, or any photo in a partial last row
        return idx % 3 === 2 || rowSize < 3;
      }
      return false;
    },
    handleTouchCancel(e) {
      if ((this.dragPending || this.isDragSelecting) && e.touches.length === 1) {
        const dx = e.touches[0].clientX - this.touchStartX;
        const dy = e.touches[0].clientY - this.touchStartY;

        // If still pending, decide: horizontal = drag-select, vertical = scroll
        if (this.dragPending && !this.isDragSelecting) {
          if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return; // not moved enough
          if (Math.abs(dy) > Math.abs(dx)) {
            // Vertical — let the page scroll
            this.dragPending = false;
            this.touchMoved = true;
            return;
          }
          // Horizontal — commit to drag-select
          this.dragPending = false;
          this.isDragSelecting = true;
          // Listen globally so auto-scroll works even outside photo tiles
          document.addEventListener('touchmove', this.onGlobalTouchMove, { passive: false });
          document.addEventListener('touchend', this.onGlobalTouchEnd);
        }

        if (!this.isDragSelecting) return;

        // Prevent native scroll during drag-select
        e.preventDefault();
        this.touchMoved = true;
        const touch = e.touches[0];
        this.startAutoScroll(touch.clientY);
        // On first drag move, select the starting photo
        if (!this.dragStartedSelecting && this.lastDragSelectedImage) {
          this.dragStartedSelecting = true;
          if (!this.selectedPhotos.has(this.lastDragSelectedImage)) {
            const next = new Set(this.selectedPhotos);
            next.add(this.lastDragSelectedImage);
            this.selectedPhotos = next;
            triggerImpact('light');
          }
        }
        this.selectTileAtPoint(touch.clientX, touch.clientY);
        return;
      }
      if (e.touches.length === 1) {
        const dx = e.touches[0].clientX - this.touchStartX;
        const dy = e.touches[0].clientY - this.touchStartY;
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          this.touchMoved = true;
        }
      } else {
        this.touchMoved = true;
      }
      if (this.touchMoved) this.cancelLongPress();
    },
    cancelLongPress() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },
    handleClick(dayIndex, imgIndex) {
      if ('ontouchstart' in window) return;
      this.handleTap(dayIndex, imgIndex);
    },
    handleTap(dayIndex, imgIndex) {
      const link = this.photos[dayIndex].photos[imgIndex]?.link;
      if (this.isSelecting) {
        if (link) this.toggleSelection(link);
      } else {
        this.openLightbox(dayIndex, imgIndex);
      }
    },

    // Lightbox
    openLightbox(dayIndex, imgIndex) {
      this.lightboxDayIndex = dayIndex;
      this.lightboxImgIndex = imgIndex;
      this.lightboxOpen = true;
      this.$nextTick(() => {
        const swiper = this.$refs.lightboxSwiper?.swiper;
        if (swiper) swiper.slideTo(this.lightboxFlatIndex, 0);
      });
    },
    closeLightbox() {
      this.lightboxOpen = false;
      this.lightboxDayIndex = null;
    },
    handleLightboxSlideChange(_isEnd, index) {
      const flatIndex = index - 1;
      let count = 0;
      for (let d = 0; d < this.photos.length; d++) {
        if (count + this.photos[d].photos.length > flatIndex) {
          this.lightboxDayIndex = d;
          this.lightboxImgIndex = flatIndex - count;
          return;
        }
        count += this.photos[d].photos.length;
      }
    },

    // Long-press popup
    showPopup(dayIndex, imgIndex) {
      const link = this.photos[dayIndex].photos[imgIndex]?.link;
      if (!link) return;
      this.popupImage = link;
      this.popupVisible = true;
    },
    closePopup() {
      this.popupVisible = false;
      this.popupImage = null;
    },
    popupSelect() {
      this.enterSelectionMode(this.popupImage);
      this.closePopup();
    },
    async popupDownload() {
      const url = this.popupImage;
      this.closePopup();
      await this.saveAndNotify(url);
    },

    // Selection mode
    enterSelectionMode(image) {
      this.isSelecting = true;
      this.selectedPhotos = image ? new Set([image]) : new Set();
    },
    exitSelectionMode() {
      this.isSelecting = false;
      this.selectedPhotos = new Set();
    },
    toggleSelection(image) {
      const next = new Set(this.selectedPhotos);
      if (next.has(image)) {
        next.delete(image);
        if (next.size === 0) {
          this.exitSelectionMode();
          return;
        }
      } else {
        next.add(image);
      }
      this.selectedPhotos = next;
    },

    // Download helpers
    saveErrorMessage(error) {
      return error?.message === 'Save timed out' ? 'Save timed out. Try again.' : 'Failed to save';
    },
    async getOrCreateAlbum() {
      const { albums } = await Media.getAlbums();
      const existing = albums.find(a => a.name === ALBUM_NAME);
      if (existing) return existing;

      await Media.createAlbum({ name: ALBUM_NAME });
      const { albums: refreshed } = await Media.getAlbums();
      return refreshed.find(a => a.name === ALBUM_NAME);
    },
    withTimeout(promise, ms) {
      return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Save timed out')), ms)),
      ]);
    },
    async downloadToTempFile(url, ext) {
      const response = await fetch(url);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const tempName = `photodrop_${Date.now()}.${ext}`;
      const result = await Filesystem.writeFile({
        path: tempName,
        data: base64,
        directory: Directory.Cache,
      });
      return result.uri;
    },
    async saveMedia(url, retry = true) {
      if (this.isNativePlatform) {
        try {
          const album = await this.getOrCreateAlbum();
          const isVid = this.isVideo(url);
          const timeout = isVid ? 120000 : 30000;
          let savePromise;
          if (isVid) {
            // Download video to temp file first (presigned URLs are too long for iOS filenames)
            const tempUri = await this.downloadToTempFile(url, 'mp4');
            savePromise = Media.saveVideo({ path: tempUri, albumIdentifier: album.identifier });
          } else {
            savePromise = Media.savePhoto({ path: url, albumIdentifier: album.identifier });
          }
          await this.withTimeout(savePromise, timeout);
        } catch (error) {
          console.error('[PhotoDrop] saveMedia error:', error?.message || error);
          if (retry && error?.message !== 'Save timed out') {
            await new Promise(resolve => setTimeout(resolve, 500));
            return this.saveMedia(url, false);
          }
          throw error;
        }
      } else {
        window.open(url, '_blank');
      }
    },
    showLightboxToast(message, isError = false) {
      if (this.lightboxToastTimer) clearTimeout(this.lightboxToastTimer);
      this.lightboxToast = message;
      this.lightboxToastError = isError;
      this.lightboxToastTimer = setTimeout(() => {
        this.lightboxToast = null;
        this.lightboxToastTimer = null;
      }, 2500);
    },
    daysUntilExpiry(expiresAt) {
      const ms = new Date(expiresAt) - new Date();
      return Math.ceil(ms / (1000 * 60 * 60 * 24));
    },
    dayExpiringSoonCount(day) {
      return day.photos.filter(p => this.daysUntilExpiry(p.expiresAt) <= 30).length;
    },
    async scrollToExpiring() {
      const idx = this.firstExpiringDayIndex;
      if (idx === -1) return;
      const ref = this.$refs['day-' + idx];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (!el) return;
      if (!this.scrollElement) await this.cacheScrollElement();
      if (this.scrollElement) {
        const containerRect = this.scrollElement.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const offset = elRect.top - containerRect.top + this.scrollElement.scrollTop - 16;
        this.scrollElement.scrollTo({ top: offset, behavior: 'smooth' });
      }
    },
    dismissTtlPopup() {
      this.showTtlPopup = false;
      this.ttlSeen = true;
      localStorage.setItem(TTL_BANNER_KEY, 'true');
    },
    async downloadCurrentLightboxPhoto() {
      const url = this.lightboxCurrentImage;
      if (!url || this.lightboxDownloading) return;
      this.lightboxDownloading = true;
      try {
        await this.saveMedia(url);
        await triggerNotification('success');
        this.showLightboxToast('Saved to Photos');
      } catch (e) {
        console.error('[PhotoDrop] Failed to save:', e);
        this.showLightboxToast(this.saveErrorMessage(e), true);
      } finally {
        this.lightboxDownloading = false;
      }
    },
    async saveAndNotify(url) {
      if (this.isVideo(url)) {
        this.showToast('Saving video...');
      }
      try {
        await this.saveMedia(url);
        await triggerNotification('success');
        this.showToast('Saved to Photos');
      } catch (e) {
        console.error('[PhotoDrop] Failed to save:', e);
        this.showToast(this.saveErrorMessage(e), '#BE3A34');
      }
    },
    async downloadSelectedPhotos() {
      this.isDownloading = true;
      const urls = [...this.selectedPhotos];
      let saved = 0;
      for (const url of urls) {
        try {
          await this.saveMedia(url);
          saved++;
        } catch (e) {
          console.error('[PhotoDrop] Failed to save photo:', e);
        }
      }
      this.isDownloading = false;
      this.exitSelectionMode();
      await triggerNotification('success');
      const message =
        saved === urls.length ? `${saved} photos saved!` : `${saved}/${urls.length} photos saved`;
      this.showToast(message);
    },
    async fetchEncryptedQrToken() {
      const token = this.$store.state.user.token;
      if (!token) return;
      this.fetchingQrToken = true;
      try {
        const { data } = await axios.get(process.env.VUE_APP_API_URL + '/photo/my-qr', {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.encryptedQrToken = data.token;
      } catch (e) {
        console.error('[PhotoDrop] Failed to fetch encrypted QR token:', e);
        // Fallback: a member's QR uses the plain personalId; a login with no member shows none.
      } finally {
        this.fetchingQrToken = false;
      }
    },
  },
};
</script>

<style scoped>
.swipe-back-blocker {
  position: fixed;
  top: 0;
  left: 0;
  width: 20px;
  height: 100%;
  z-index: 9999;
}

.photodrop-header {
  padding-top: env(safe-area-inset-top, 0px);
}

.photodrop-floating-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  gap: 4px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 50px;
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.2);
}

.photodrop-float-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 50px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #42362b;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.photodrop-float-tab--active {
  background: #fec629;
  box-shadow:
    0 4px 12px rgba(254, 198, 41, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.photo-tile {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: manipulation;
}

.photo-tile--selectable::after {
  content: '';
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 2px solid #3d3a35;
  pointer-events: none;
  z-index: 0;
}

.photo-tile--selectable .selection-check {
  z-index: 1;
}

.lightbox-swiper-area :deep(.relative),
.lightbox-swiper-area :deep(.swiper),
.lightbox-swiper-area :deep(.swiper-wrapper),
.lightbox-swiper-area :deep(.swiper-slide) {
  height: 100%;
}
</style>

<style>
.peek-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: peek-bg-in 0.2s ease-out;
}

.peek-content {
  width: 100%;
  -webkit-user-select: none;
  user-select: none;
  max-width: 340px;
  animation: peek-scale-in 0.25s cubic-bezier(0.2, 0.9, 0.3, 1.05);
}

.peek-image {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3);
}

.peek-actions {
  margin-top: 12px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.peek-action {
  width: 100%;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 500;
  color: #242425;
  background: none;
  border: none;
}

.peek-action:active {
  background: #f3f2ef;
}

.peek-divider {
  height: 1px;
  background: #eeece8;
  margin: 0 16px;
}

@keyframes peek-bg-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes peek-scale-in {
  from {
    transform: scale(0.85);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.photobooth-otp-input {
  width: 44px;
  height: 56px;
  border-radius: 10px;
  border: 1px solid #d8dadc;
  display: inline-block;
  text-align: center;
  background: #ffffff;
  font-size: 22px;
  font-weight: 700;
  margin: 0 3px;
  caret-color: transparent;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

.photobooth-otp-input:focus {
  border: 1.5px solid #3d3a35;
  outline: none;
}

.photobooth-otp-input::selection {
  background: transparent;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

/* `.scanner-active` now lives in theme/scanner-transparency.css. It was global
   from here only because the router imports this page statically — an accident
   the Next Steps approval modal then inherited and was broken by. */

.lightbox-toast {
  position: absolute;
  top: 80px;
  z-index: 10;
  left: 16px;
  right: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  animation: lightbox-toast-in 0.2s ease-out;
  pointer-events: none;
}

@keyframes lightbox-toast-in {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.photodrop-toast {
  --border-radius: 8px;
  --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  --start: 16px;
  --end: 16px;
  font-weight: 500;
  font-size: 16px;
}

.scanner-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
}

.scanner-mask {
  display: none;
}

.scanner-back-btn {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 16px);
  left: 16px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  z-index: 2;
}

.scanner-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80vw;
  height: 80vw;
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 24px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

.scanner-bottom-ui {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(50% + 170px);
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
